import { z } from "zod";
import { discordSnowflakeSchema, normalizeAllowedParentChannelIds, utcTimestampSchema, } from "./guild-settings.js";
import { guildEntitlementProjectionSchema } from "./guild-entitlements.js";
import { guildAssistantAccessSchema, guildCharacterLinksSchema, guildInstructionsSchema, guildPlayerAliasesSchema, guildToneSchema, } from "./guild-customization.js";
const discordImageHashSchema = z.string().min(1).max(128);
const DISCORD_PERMISSION_MAX = 18446744073709551615n;
const MAX_DASHBOARD_CHANNELS = 500;
const discordPermissionSchema = z
    .string()
    .regex(/^(0|[1-9][0-9]{0,19})$/)
    .refine((value) => BigInt(value) <= DISCORD_PERMISSION_MAX, {
    message: "Permission value exceeds the unsigned 64-bit range",
});
const discordInstallUrlSchema = z
    .string()
    .url()
    .superRefine((value, context) => {
    const url = new URL(value);
    if (url.protocol !== "https:" ||
        url.hostname !== "discord.com" ||
        url.port !== "" ||
        url.pathname !== "/oauth2/authorize" ||
        url.username !== "" ||
        url.password !== "" ||
        url.hash !== "") {
        context.addIssue({
            code: "custom",
            message: "Invalid Discord install URL",
        });
    }
});
const strictSnowflakeListSchema = z
    .array(discordSnowflakeSchema)
    .max(MAX_DASHBOARD_CHANNELS)
    .superRefine((values, context) => {
    if (new Set(values).size !== values.length) {
        context.addIssue({
            code: "custom",
            message: "Channel IDs must be unique",
        });
    }
});
export const managedGuildSnapshotElementSchema = z
    .object({
    id: discordSnowflakeSchema,
    name: z.string().min(1).max(100),
    icon: discordImageHashSchema.nullable(),
    owner: z.boolean(),
    permissions: discordPermissionSchema,
})
    .strict();
export const managedGuildSnapshotSchema = z
    .array(managedGuildSnapshotElementSchema)
    .max(200)
    .superRefine((guilds, context) => {
    if (new Set(guilds.map((guild) => guild.id)).size !== guilds.length) {
        context.addIssue({
            code: "custom",
            message: "Snapshot guild IDs must be unique",
        });
    }
});
export const dashboardErrorSchema = z
    .object({
    code: z.string().trim().min(1).max(100),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    retryAfterSeconds: z.int().positive().optional(),
})
    .strict();
export const publicStatusSchema = z
    .object({
    status: z.enum(["ok", "degraded"]),
})
    .strict();
export const currentUserProfileSchema = z
    .object({
    id: discordSnowflakeSchema,
    username: z.string().min(2).max(32),
    globalName: z.string().min(1).max(32).nullable(),
    avatar: discordImageHashSchema.nullable(),
})
    .strict();
const dashboardGuildSummaryBaseSchema = z.object({
    id: discordSnowflakeSchema,
    name: z.string().min(1).max(100),
    icon: discordImageHashSchema.nullable(),
    owner: z.boolean(),
    managed: z.boolean(),
    permissions: discordPermissionSchema,
});
export const dashboardGuildSummarySchema = z.discriminatedUnion("installed", [
    dashboardGuildSummaryBaseSchema.extend({
        installed: z.literal(true),
        installUrl: z.null(),
    }),
    dashboardGuildSummaryBaseSchema.extend({
        installed: z.literal(false),
        installUrl: discordInstallUrlSchema,
    }),
]);
export const dashboardGuildListSchema = z
    .object({
    guilds: z.array(dashboardGuildSummarySchema).max(200),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.guilds.map((guild) => guild.id)).size !==
        value.guilds.length) {
        context.addIssue({ code: "custom", message: "Guild IDs must be unique" });
    }
})
    .transform((value) => ({
    guilds: [...value.guilds].sort((left, right) => {
        const nameOrder = left.name.localeCompare(right.name);
        if (nameOrder !== 0)
            return nameOrder;
        const lengthOrder = left.id.length - right.id.length;
        return lengthOrder === 0 ? left.id.localeCompare(right.id) : lengthOrder;
    }),
}));
export const dashboardChannelSchema = z
    .object({
    id: discordSnowflakeSchema,
    name: z.string().min(1).max(100),
    type: z.int().min(0).max(255),
    parentId: discordSnowflakeSchema.nullable(),
    position: z.int().nonnegative(),
    selectable: z.boolean(),
})
    .strict();
export const dashboardChannelListSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    channels: z.array(dashboardChannelSchema).max(MAX_DASHBOARD_CHANNELS),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.channels.map((channel) => channel.id)).size !==
        value.channels.length) {
        context.addIssue({
            code: "custom",
            message: "Channel IDs must be unique",
        });
    }
});
export const dashboardBotStatusSchema = z.discriminatedUnion("online", [
    z
        .object({ online: z.literal(true), lastSeenAt: utcTimestampSchema })
        .strict(),
    z
        .object({
        online: z.literal(false),
        lastSeenAt: utcTimestampSchema.nullable(),
    })
        .strict(),
]);
const installedGuildSettingsSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    mentionsEnabled: z.boolean(),
    allowedChannelIds: strictSnowflakeListSchema.transform(normalizeAllowedParentChannelIds),
    revision: z.int().positive(),
    updatedAt: utcTimestampSchema,
    installationState: z.literal("installed"),
    botStatus: dashboardBotStatusSchema,
})
    .strict();
const uninstalledGuildSettingsSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    installationState: z.literal("not_installed"),
})
    .strict();
export const dashboardGuildSettingsSchema = z.discriminatedUnion("installationState", [installedGuildSettingsSchema, uninstalledGuildSettingsSchema]);
export const dashboardGuildSettingsConflictSchema = z
    .object({
    code: z.literal("guild_settings_revision_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: installedGuildSettingsSchema,
})
    .strict();
export const dashboardGuildSettingsPatchSchema = z
    .object({
    mentionsEnabled: z.boolean(),
    allowedChannelIds: strictSnowflakeListSchema,
    revision: z.int().positive(),
})
    .strict()
    .transform((value) => ({
    ...value,
    allowedChannelIds: normalizeAllowedParentChannelIds(value.allowedChannelIds),
}));
export const dashboardGuildTrialActivationSchema = z
    .object({
    expectedRevision: z.int().positive(),
})
    .strict();
export const dashboardGuildEntitlementConflictSchema = z
    .object({
    code: z.literal("guild_entitlement_revision_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: guildEntitlementProjectionSchema,
})
    .strict();
export const dashboardGuildInstructionsConflictSchema = z
    .object({
    code: z.literal("guild_instructions_revision_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: guildInstructionsSchema,
})
    .strict();
export const dashboardGuildAssistantAccessConflictSchema = z
    .object({
    code: z.literal("guild_assistant_access_revision_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: guildAssistantAccessSchema,
})
    .strict();
export const dashboardGuildPlayerAliasesConflictSchema = z
    .object({
    code: z.literal("guild_player_aliases_revision_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: guildPlayerAliasesSchema,
})
    .strict();
export const dashboardGuildToneConflictSchema = z
    .object({
    code: z.literal("guild_tone_revision_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: guildToneSchema,
})
    .strict();
export const dashboardGuildCharacterLinksConflictSchema = z
    .object({
    code: z.literal("guild_character_links_revision_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: guildCharacterLinksSchema,
})
    .strict();
/*
 * An uninstalled guild has no persisted settings row, revision, update time, or
 * bot heartbeat. Those fields only exist on the installed response variant.
 */
export const dashboardUninstalledGuildSettingsSchema = uninstalledGuildSettingsSchema;
//# sourceMappingURL=dashboard.js.map